import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import api, { assetUrl } from "@/lib/api";
import { ReadingProgress } from "@/components/ReadingProgress";
import { ShareRail } from "@/components/ShareRail";
import RelatedArticles from "@/components/RelatedArticles";
import { calmEase } from "@/components/motion/Reveal";
import { applySeo, SITE_URL } from "@/lib/seo";
import { ArrowLeft } from "@phosphor-icons/react";

const tagSlug = (t) => t.replace(/[^a-zA-Z0-9\s-]/g, "").trim().toLowerCase().replace(/[\s_-]+/g, "-");

function formatDate(d) {
    try { return new Date(d).toLocaleDateString("en-US", { month: "long", day: "2-digit", year: "numeric" }); }
    catch { return ""; }
}

// Custom renderers so markdown images use the assetUrl() resolver and inherit
// the Tizona figure styling. Everything else uses Tailwind via .prose-tz.
const markdownComponents = {
    img: ({ src, alt }) => (
        <figure className="my-8" data-testid="post-inline-image">
            <img
                src={assetUrl(src)}
                alt={alt || ""}
                className="w-full h-auto border border-zinc-800"
                loading="lazy"
            />
            {alt ? (
                <figcaption className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    {alt}
                </figcaption>
            ) : null}
        </figure>
    ),
    a: ({ href, children }) => (
        <a href={href} target="_blank" rel="noreferrer" className="text-[#FFD000] underline underline-offset-4 hover:text-white">
            {children}
        </a>
    ),
};

const REMARK_PLUGINS = [remarkGfm];
const PARALLAX_OFFSET = ["start start", "end start"];
const HEADLINE_INITIAL = { opacity: 0, y: 28 };
const CONTENT_INITIAL = { opacity: 0, y: 18 };
const VISIBLE = { opacity: 1, y: 0 };
const HEADLINE_TRANSITION = { duration: 0.7, ease: calmEase, delay: 0.1 };
const CONTENT_TRANSITION = { duration: 0.7, ease: calmEase, delay: 0.25 };

function buildJsonLd(post, cover) {
    return {
        "@context": "https://schema.org",
        "@type": "NewsArticle",
        "headline": post.title,
        "description": post.excerpt,
        "image": [cover],
        "datePublished": new Date(post.created_at).toISOString(),
        "dateModified": new Date(post.updated_at || post.created_at).toISOString(),
        "author": [{ "@type": "Person", "name": post.author_name }],
        "publisher": {
            "@type": "Organization",
            "name": "Tizona Tech",
            "logo": {
                "@type": "ImageObject",
                "url": `${SITE_URL}/android-chrome-512x512.png`,
            },
        },
        "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `${SITE_URL}/post/${post.slug}`,
        },
        "articleSection": post.category,
        ...(post.tags?.length ? { "keywords": post.tags.join(", ") } : {}),
    };
}

export default function PostDetail() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const coverRef = useRef(null);
    const reduceMotion = useReducedMotion();
    const { scrollYProgress } = useScroll({ target: coverRef, offset: PARALLAX_OFFSET });
    const coverY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/posts/${slug}`);
                setPost(data);
            } catch {
                setNotFound(true);
            } finally { setLoading(false); }
        })();
    }, [slug]);

    useEffect(() => {
        if (!post) return;
        const cover = post.cover_image ? assetUrl(post.cover_image) : `${SITE_URL}/og-image.png`;
        applySeo({
            title: `${post.title} — Tizona Tech`,
            description: post.excerpt,
            path: `/post/${post.slug}`,
            type: "article",
            image: cover,
            publishedTime: new Date(post.created_at).toISOString(),
            modifiedTime: new Date(post.updated_at || post.created_at).toISOString(),
            section: post.category,
            jsonLd: buildJsonLd(post, cover),
        });
    }, [post]);

    if (loading) {
        return <div className="max-w-3xl mx-auto px-6 py-24 text-center font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">Loading...</div>;
    }
    if (notFound || !post) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="post-not-found">
                <div className="font-display text-6xl text-white mb-4">404</div>
                <div className="text-zinc-400 mb-8">This article walked off into the void.</div>
                <Link to="/" className="btn-primary inline-block">Back Home</Link>
            </div>
        );
    }

    return (
        <article className="bg-[#0A0A0A]" data-testid={`post-detail-${post.slug}`}>
            <ReadingProgress />
            {/* Cover */}
            <div ref={coverRef} className="relative h-[60vh] md:h-[70vh] overflow-hidden border-b border-zinc-800">
                {post.cover_image && (
                    <motion.img
                        src={assetUrl(post.cover_image)}
                        alt={post.title}
                        style={reduceMotion ? undefined : { y: coverY }}
                        className="w-full h-full object-cover scale-110"
                    />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-transparent" />
                <div className="absolute inset-0 flex items-end">
                    <motion.div
                        className="max-w-[1100px] mx-auto px-6 md:px-10 pb-12 w-full"
                        initial={reduceMotion ? false : HEADLINE_INITIAL}
                        animate={VISIBLE}
                        transition={HEADLINE_TRANSITION}
                    >
                        <Link to={`/category/${post.category}`} className="inline-block mb-6" data-testid="post-category-link">
                            <span className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] bg-[#FFD000] text-black px-3 py-1.5">
                                {post.category}
                            </span>
                        </Link>
                        <h1 className="font-display text-white text-4xl md:text-7xl leading-[0.9] tracking-tight max-w-4xl">
                            {post.title}
                        </h1>
                        <div className="mt-6 font-mono text-xs uppercase tracking-[0.25em] text-zinc-400">
                            {post.author_name} <span className="mx-2 text-zinc-700">/</span> {formatDate(post.created_at)}
                        </div>
                        {post.tags?.length > 0 && (
                            <div className="mt-5 flex flex-wrap gap-2" data-testid="post-tags">
                                {post.tags.map(t => {
                                    const s = tagSlug(t);
                                    return (
                                        <Link key={s} to={`/tag/${s}`} data-testid={`post-tag-${s}`}
                                              className="font-mono text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 border border-zinc-600 bg-black/40 backdrop-blur-sm text-zinc-200 hover:border-[#FFD000] hover:text-[#FFD000] transition-colors">
                                            #{t}
                                        </Link>
                                    );
                                })}
                            </div>
                        )}
                    </motion.div>
                </div>
            </div>

            <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-16 grid md:grid-cols-12 gap-12">
                <ShareRail />

                <motion.div
                    className="md:col-span-10"
                    initial={reduceMotion ? false : CONTENT_INITIAL}
                    animate={VISIBLE}
                    transition={CONTENT_TRANSITION}
                >
                    <p className="text-2xl md:text-3xl font-display text-white leading-[1.2] mb-10 pb-10 border-b border-zinc-800">
                        {post.excerpt}
                    </p>

                    {post.video_url && (
                        <div className="relative aspect-video w-full mb-10 border border-zinc-800" data-testid="post-video-embed">
                            <iframe
                                src={post.video_url}
                                title={post.title}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowFullScreen
                                className="absolute inset-0 w-full h-full"
                            />
                        </div>
                    )}

                    <div className="prose-tz max-w-none" data-testid="post-content">
                        <ReactMarkdown
                            remarkPlugins={REMARK_PLUGINS}
                            components={markdownComponents}
                        >
                            {post.content}
                        </ReactMarkdown>
                    </div>

                    <div className="mt-16 pt-8 border-t border-zinc-800">
                        <Link to={`/category/${post.category}`} className="btn-secondary inline-flex items-center gap-2" data-testid="post-back-link">
                            <ArrowLeft size={16} weight="bold" /> More in {post.category}
                        </Link>
                    </div>
                </motion.div>
            </div>
            <RelatedArticles slug={post.slug} category={post.category} />
        </article>
    );
}
